// 介錯「執行の咆哮」の空気の衝撃波 (既定・旧は DEV ?roarAir=0・src/exec_roar_air.ts) の見た目 (2026-10-09 夜)。描くだけ・当たりは触らない。
//
//   ユーザー (10-09): 「音波の輪は消して良い。今のところどれもだいぶ見えにくいな、なんかちかちがしてるなぁって感じ」
//     見せ方は「空気の圧の系統で」(戴冠獣の吠え src/hound_howl_fx.ts)。見本 = C:/dev/LUNAIRE-notes/assets/2026-10-09-roar-airwave/fx/roar_airwave_v1.png
//   出す物 (前縁ごと・左右とも。前縁の位置は当たりと同じ式 = src/roar_air_geometry.ts):
//     ① 背景の歪み: 前縁のまわりの背景を「外へ押す大きなふくらみ1つ」で横に歪ませる。時間で震わせない (位置が動くだけ)。
//        床から頭の少し上までが全部の強さ・その上と床の近くはなだらかに弱める。
//     ② 掛け布・カーテンの裾がめくれる: 前縁が通る所だけ、背景の裾の高さを少し持ち上げ、通り過ぎた後ろでゆっくり戻す。
//     ③ 蝋燭の炎が倒れる: 背景に描いてある炎だけ、前縁が通ると外へ傾き、ゆっくり戻る。
//     ④ 前縁の霞: 縦長のやわらかい銀白〜淡い月色の明るみ (輪郭も線も無い)。
//     ⑤ 足もとの砂ぼこり: 前縁の足もとで膝より低い波頭が外へ転がり、通り過ぎた後ろでゆっくり沈む。
//   強さは波1 小 → 波3 大 (歪みの大きさ・ほこりの高さ)。
//   ちかちかにしない約束: 速い明滅なし・細かい縞なし・毎フレーム変わる乱数なし (粒は波と番号で決める)・床の目地を光らせない。
//   描画の継ぎ目が線に見えないよう、貼り直す区切りはすべて整数の画素に置く。
//   型① = ゲームが止まっている間 (phase≠playing) は呼び出し側が描かない。型② = 強さは前縁が端に着く前に 0 まで落ちる。
import { ARENA } from './balance'
import { hash01 } from './fx_kit'
import { airWaveEdgePx, airWaveRadiusPx } from './roar_air_geometry'
import type { ActiveHit } from './types'
import { MIST_FORMS, drawMat, dustReady, dustSprite, mistReady, mistSprite, preloadExecFxMats, roarWallReady, roarWallSprite, tinted } from './exec_fx_mats'

// 霞の見た目 (10-29 作者 exec-fx-q5 で「直したい」に印): 比較版 = 筆の靄の絵を前縁の後ろへ立てる (DEV ?execRoarHaze=mist か ?execFx=new)。
//   配布版の既定は前の版 (ぼかしたグラデーションの幕 veil)。
export type RoarHazeLook = 'mist' | 'veil'
let hazeLook: RoarHazeLook = 'veil'
export function setRoarHazeLook(v: string | null): void { hazeLook = v === 'mist' ? 'mist' : 'veil' }
// 10-30 作者 roar3「衝撃波ってこんなにうねうねしてない・遅いから漂って見える」→ roar4「新しい版を入れる」: 細く硬い輪が配布版の既定。
//   靄・幕をやめ、前縁は幅の狭い強い歪みの帯と、床の土ぼこりの絵 (drawRingDust) だけ。DEV ?execRoarWave=old で前の幕。
//   速さは src/exec_roar_air.ts の ROAR_AIR_RING_SPEED。見本 = docs/research/2026-10-30-exec-roar-shockwave.md
//   土ぼこりの絵は exec_floor_fx.ts が読み込み時に先読みする (preloadExecFxMats)。
let ringLook = true
export function setRoarWaveRing(on: boolean): void { ringLook = on; if (on) preloadExecFxMats() }
export function roarWaveRingOn(): boolean { return ringLook }
// 10-31 比較版 (DEV ?execRoarWave=wall): 輪の見た目に、前縁が押す塵の帯の絵 (drawRingWall) を足す。配布版の既定は輪のまま。
let wallLook = false
export function setRoarWaveWall(on: boolean): void { wallLook = on; if (on) preloadExecFxMats() }
const RING = { warpBack: 34, warpFront: 12, warpMul: 1.15 } as const

export type AirFront = {
  /** 前縁の世界 x (壁で止めた位置)。 */
  x: number
  side: 1 | -1
  originX: number
  /** 見た目の強さ 0..1 (出てすぐ上がり、端の手前でなだらかに 0 へ)。 */
  k: number
  level: 1 | 2 | 3
  /** 中心から前縁までの半径 (壁で止めた値)。砂ぼこりの粒の基準。 */
  r: number
  /** 壁に着いてからの距離 (px・着く前は 0)。 */
  overWall: number
  /** 前縁が止まる半径 (届く範囲の端)。砂ぼこりの粒はこの間に決まった位置で並ぶ。 */
  edge: number
  hitId: number
}

// 10-09 の最初の撮影で「ほとんど見えない」(前の版も「だいぶ見えにくい」と言われた) → 強めに置いた。
const GAIN = {
  1: { warpPx: 13, liftPx: 6, dustPx: 12, haze: 0.26, lean: 0.6, dustA: 0.5 },
  2: { warpPx: 18, liftPx: 8, dustPx: 17, haze: 0.34, lean: 0.85, dustA: 0.6 },
  3: { warpPx: 24, liftPx: 11, dustPx: 24, haze: 0.44, lean: 1, dustA: 0.7 },
} as const

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

/** 前後で幅の違う、端で必ず 0 になる山 (cos²)。u = 進む向きに正 (前縁より前)。 */
export function airBump(u: number, back: number, front: number): number {
  if (u >= 0) return u < front ? Math.cos((Math.PI / 2) * (u / front)) ** 2 : 0
  return -u < back ? Math.cos((Math.PI / 2) * (u / back)) ** 2 : 0
}

/** 前縁の強さ。出てから約 1 割で上がり切り、届く範囲の 7 割を過ぎたらなだらかに下げ、端で 0。 */
export function airFrontStrength(u01: number): number {
  return smooth(0, 0.08, u01) * (1 - smooth(0.7, 1, u01))
}

/** いま見えている前縁 (左右)。壁で止め、壁に着いたら 90px ぶんの時間でなだらかに消す。 */
export function roarAirFronts(hits: readonly ActiveHit[], walls: { leftWall: number; rightWall: number }): AirFront[] {
  const out: AirFront[] = []
  for (const h of hits) {
    if (!h.airWave || h.follow) continue
    if (h.elapsedMs < h.telegraphMs || h.elapsedMs >= h.telegraphMs + h.activeMs) continue
    const r = airWaveRadiusPx({ elapsedMs: h.elapsedMs, telegraphMs: h.telegraphMs, airWave: h.airWave })
    const base = airFrontStrength(r / airWaveEdgePx(h.airWave))
    if (base <= 0.002) continue
    for (const side of [-1, 1] as const) {
      const toWall = side > 0 ? walls.rightWall - h.airWave.originX : h.airWave.originX - walls.leftWall
      const rv = Math.max(0, Math.min(r, toWall))
      const overWall = Math.max(0, r - toWall)
      const k = base * (1 - smooth(0, 90, overWall))
      if (k <= 0.002) continue
      out.push({ x: h.airWave.originX + side * rv, side, originX: h.airWave.originX, k, level: h.airWave.level, r: rv, overWall, edge: airWaveEdgePx(h.airWave), hitId: h.id })
    }
  }
  return out
}

// ===== ①②③ 背景の層 =====
// 背景の絵の置き方 (render.ts の drawBackground と同じ): 960×540 換算の絵の座標 (u,v) → 世界 (shiftX + u·scale, floorY − floorY·scale + v·scale)。
export type BgPlace = { shiftX: number; scale: number }

// 施療院の背景 (public/bg/executioner-hospice.png) の、960×540 換算の位置。
//   裾: 寝台の掛け布の下の縁 ≒ 345〜355・カーテンの裾 ≒ 290〜350。持ち上げの山を 340 に置き、250〜372 の間だけ動かす。
//   蝋燭: 炎の明るい画素の塊 (2026-10-09 に絵から測った)。
const HEM = { top: 250, mid: 340, bot: 372 } as const
const CANDLES: ReadonlyArray<{ x: number; y: number }> = [
  { x: 141, y: 289 }, { x: 356, y: 278 }, { x: 626, y: 276 }, { x: 901, y: 274 },
]

// 歪みの形 (世界 px): 前縁の前 64px・後ろ 110px の山 (外へ押す)。裾の持ち上げは前縁の 20px 後ろが山・後ろへ長く残る (布がゆっくり落ちる)。
const WARP_BACK = 110
const WARP_FRONT = 80
const LIFT_LAG = 20
const LIFT_BACK = 150
const LIFT_FRONT = 70
const WIN_BACK = LIFT_LAG + LIFT_BACK + 4
const WIN_FRONT = WARP_FRONT + 4
const SEGMENTS = 16
const FINE_SEGMENTS = 10
const HEM_COLUMNS = 110
// 炎の傾き: 前縁が通った 25px 後ろが山・後ろ 220px かけて戻る (約 0.5 秒)。
const LEAN_LAG = 25
const LEAN_BACK = 220
const LEAN_FRONT = 30
const LEAN_TIP_PX = 7

let sbuf: HTMLCanvasElement | null = null
let tbuf: HTMLCanvasElement | null = null
function buf(c: HTMLCanvasElement | null, w: number, h: number): HTMLCanvasElement {
  const b = c ?? document.createElement('canvas')
  if (b.width < w || b.height < h) { b.width = Math.max(b.width, w); b.height = Math.max(b.height, h) }
  return b
}

/**
 * 背景の層 (drawBackground の直後・世界やキャラを描く前に呼ぶ)。背景だけを歪ませる (二人と HUD は動かない)。
 * bossH = ボスの描く高さ (頭の高さの目安)。bg = 背景の絵の置き方 (横長の背景の時は null = 裾と炎は動かさない)。
 */
export function applyRoarAirBackground(
  ctx: CanvasRenderingContext2D, fronts: readonly AirFront[], cameraX: number, bossH: number, bg: BgPlace | null,
): void {
  if (fronts.length === 0) return
  const canvas = ctx.canvas
  const m = ctx.getTransform()
  const ma = m.a
  const floorY = ARENA.floorY
  const toSY = (yw: number): number => Math.round(m.f + yw * m.d)
  // 縦の強さの段 (世界 y)。頭の少し上まで全部・その上 110px で 0 へ・床の手前 72px から床の 8px 上で 0 へ。
  const yHead = floorY - bossH * 1.02
  const yTop = yHead - 90
  const yFloorFull = floorY - 72
  const yFloorEnd = floorY - 8
  // 弱める所は画面で 3px ごとの細い段にする (10-09 の撮影: 段が粗いとカーテンの縁が階段状に欠けて見えた)。
  const bands: Array<{ y0: number; y1: number; f: number; fine: boolean }> = []
  const ROW = 3
  const fade = (ya: number, yb: number, fFrom: number, fTo: number): void => {
    const s0 = toSY(ya), s1 = toSY(yb)
    for (let y = s0; y < s1; y += ROW) {
      const mid = (y + Math.min(s1, y + ROW)) / 2
      const t = (mid - s0) / Math.max(1, s1 - s0)
      const e = t * t * (3 - 2 * t)
      bands.push({ y0: y, y1: Math.min(s1, y + ROW), f: fFrom + (fTo - fFrom) * e, fine: true })
    }
  }
  fade(yTop, yHead, 0, 1)
  bands.push({ y0: toSY(yHead), y1: toSY(yFloorFull), f: 1, fine: false })
  fade(yFloorFull, yFloorEnd, 1, 0)
  const Y0 = Math.max(0, toSY(yTop))
  const Y1 = Math.min(canvas.height, toSY(yFloorEnd))
  if (Y1 - Y0 < 4) return
  // 裾の高さ (画面 y)。
  const bgY = (v: number): number => bg ? floorY - floorY * bg.scale + v * bg.scale : NaN
  const hemTop = bg ? toSY(bgY(HEM.top)) : 0
  const hemMid = bg ? toSY(bgY(HEM.mid)) : 0
  const hemBot = bg ? Math.min(Y1, toSY(bgY(HEM.bot))) : 0

  for (const fr of fronts) {
    const g = GAIN[fr.level]
    const fS = m.e + (fr.x - cameraX) * ma
    const wBack = ringLook ? RING.warpBack : WARP_BACK
    const wFront = ringLook ? RING.warpFront : WARP_FRONT
    const a = fS + fr.side * -WIN_BACK * ma
    const b = fS + fr.side * WIN_FRONT * ma
    const X0 = Math.max(0, Math.floor(Math.min(a, b)))
    const X1 = Math.min(canvas.width, Math.ceil(Math.max(a, b)))
    const W = X1 - X0
    const H = Y1 - Y0
    if (W < 4) continue
    sbuf = buf(sbuf, W, H)
    tbuf = buf(tbuf, W, H)
    const sctx = sbuf.getContext('2d')
    const tctx = tbuf.getContext('2d')
    if (!sctx || !tctx) return
    // 歪ませる前の背景 (この前縁より先に描いた前縁の歪みも含む) を写す。
    sctx.setTransform(1, 0, 0, 1, 0, 0)
    sctx.globalCompositeOperation = 'copy'
    sctx.drawImage(canvas, X0, Y0, W, H, 0, 0, W, H)
    sctx.globalCompositeOperation = 'source-over'
    // 下地 = 歪ませない絵 (区切りの端の画素がにじんでも元の絵が出るだけ)。
    tctx.setTransform(1, 0, 0, 1, 0, 0)
    tctx.globalCompositeOperation = 'copy'
    tctx.drawImage(sbuf, 0, 0, W, H, 0, 0, W, H)
    tctx.globalCompositeOperation = 'source-over'
    const amp = g.warpPx * fr.k * (ringLook ? RING.warpMul : 1)
    // ① 横の歪み: 区切った区間ごとに伸び縮みで貼る (区間の継ぎ目は連続 = 縞にならない)。行き先の区切りは整数の画素。
    for (const band of bands) {
      const by0 = Math.max(Y0, band.y0) - Y0
      const by1 = Math.min(Y1, band.y1) - Y0
      if (by1 <= by0) continue
      const A = amp * band.f
      if (A * ma < 0.25) continue
      // 歪むのは山の中 (後ろ WARP_BACK 〜 前 WARP_FRONT) だけ。外は下地 (歪ませない絵) のまま。細い段は区切りを減らす。
      const segs = band.fine ? FINE_SEGMENTS : SEGMENTS
      let prevS = 0, prevD = 0
      for (let j = 0; j <= segs; j += 1) {
        const u = -wBack + (wBack + wFront) * j / segs
        const sx = fS + fr.side * u * ma - X0
        const dx = Math.round(fS + fr.side * (u + A * airBump(u, wBack, wFront)) * ma - X0)
        if (j > 0) {
          const s0 = Math.min(prevS, sx), s1 = Math.max(prevS, sx)
          const d0 = Math.min(prevD, dx), d1 = Math.max(prevD, dx)
          drawClippedH(tctx, sbuf, s0, s1, d0, d1, by0, by1 - by0, W)
        }
        prevS = sx; prevD = dx
      }
    }
    // ② 裾の持ち上げ (背景の絵が分かる時だけ): 縦に3つ (動かさない上・縮める・伸ばす) に分けて、列ごとに貼る。
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    const lift = bg && !ringLook ? g.liftPx * fr.k * ma : 0
    if (lift >= 0.3 && hemBot - hemMid > 2 && hemMid - hemTop > 2) {
      const hT = hemTop - Y0, hM = hemMid - Y0, hB = hemBot - Y0
      // 裾より上と下はそのまま
      if (hT > 0) ctx.drawImage(tbuf, 0, 0, W, hT, X0, Y0, W, hT)
      if (hB < H) ctx.drawImage(tbuf, 0, hB, W, H - hB, X0, Y0 + hB, W, H - hB)
      for (let c = 0; c < HEM_COLUMNS; c += 1) {
        const cx0 = Math.round(W * c / HEM_COLUMNS)
        const cx1 = Math.round(W * (c + 1) / HEM_COLUMNS)
        const cw = cx1 - cx0
        if (cw <= 0) continue
        const u = fr.side * ((X0 + (cx0 + cx1) / 2) - fS) / ma
        const L = Math.round(lift * airBump(u + LIFT_LAG, LIFT_BACK, LIFT_FRONT) * 2) / 2
        if (L < 0.5) {
          ctx.drawImage(tbuf, cx0, hT, cw, hB - hT, X0 + cx0, Y0 + hT, cw, hB - hT)
          continue
        }
        const mid = Math.round(hM - L)
        ctx.drawImage(tbuf, cx0, hT, cw, hM - hT, X0 + cx0, Y0 + hT, cw, mid - hT)
        ctx.drawImage(tbuf, cx0, hM, cw, hB - hM, X0 + cx0, Y0 + mid, cw, hB - mid)
      }
    } else {
      ctx.drawImage(tbuf, 0, 0, W, H, X0, Y0, W, H)
    }
    ctx.restore()
  }
  // ③ 炎の傾き (前縁を全部描いた後に・炎ごとに足し合わせる)。
  if (bg) drawCandleLean(ctx, fronts, cameraX, bg)
}

// 区間 [s0,s1] (写した絵の x) を [d0,d1] へ伸び縮みで貼る。写した絵の外にはみ出す所は比で切る。
function drawClippedH(
  t: CanvasRenderingContext2D, src: HTMLCanvasElement, s0: number, s1: number, d0: number, d1: number, y: number, h: number, W: number,
): void {
  if (s1 - s0 < 1e-3 || d1 - d0 < 1) return
  let a0 = s0, a1 = s1, b0 = d0, b1 = d1
  const k = (b1 - b0) / (a1 - a0)
  if (a0 < 0) { b0 += (0 - a0) * k; a0 = 0 }
  if (a1 > W) { b1 -= (a1 - W) * k; a1 = W }
  if (a1 - a0 < 1e-3 || b1 - b0 < 0.5) return
  t.drawImage(src, a0, y, a1 - a0, h, b0, y, b1 - b0, h)
}

let cbuf: HTMLCanvasElement | null = null
function drawCandleLean(ctx: CanvasRenderingContext2D, fronts: readonly AirFront[], cameraX: number, bg: BgPlace): void {
  const m = ctx.getTransform()
  const ma = m.a
  const floorY = ARENA.floorY
  for (const c of CANDLES) {
    const wx = bg.shiftX + c.x * bg.scale
    const wy = floorY - floorY * bg.scale + c.y * bg.scale
    const sx = m.e + wx * ma           // 背景はカメラの横移動を視差 (shiftX) で受ける
    let lean = 0
    for (const fr of fronts) {
      const fS = m.e + (fr.x - cameraX) * ma
      const u = fr.side * (sx - fS) / ma
      lean += fr.side * GAIN[fr.level].lean * fr.k * airBump(u + LEAN_LAG, LEAN_BACK, LEAN_FRONT)
    }
    const tip = lean * LEAN_TIP_PX * bg.scale * ma
    if (Math.abs(tip) < 0.4) continue
    // 炎の箱: 中心から上 22・下 6・左右 10 (世界)。左右に余白 14 を足して、行ごとに台形に押す (端は動かさない)。
    const pad = 14 * bg.scale * ma
    const half = 10 * bg.scale * ma
    const top = Math.round(m.f + (wy - 22 * bg.scale) * m.d)
    const bot = Math.round(m.f + (wy + 6 * bg.scale) * m.d)
    const x0 = Math.round(sx - half - pad)
    const x1 = Math.round(sx + half + pad)
    const W = x1 - x0, H = bot - top
    if (W <= 2 || H <= 2) continue
    cbuf = buf(cbuf, W, H)
    const cctx = cbuf.getContext('2d')
    if (!cctx) continue
    cctx.setTransform(1, 0, 0, 1, 0, 0)
    cctx.globalCompositeOperation = 'copy'
    cctx.drawImage(ctx.canvas, x0, top, W, H, 0, 0, W, H)
    cctx.globalCompositeOperation = 'source-over'
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    const ROW = 2
    for (let y = 0; y < H; y += ROW) {
      const rh = Math.min(ROW, H - y)
      // 根元 (下) は動かさず、先 (上) ほど大きく倒す。
      const hUp = 1 - (y + rh / 2) / H
      const s = Math.round(tip * Math.pow(hUp, 1.4) * 2) / 2
      if (Math.abs(s) < 0.5) continue
      const p = Math.round(pad), q = W - Math.round(pad)
      // 左の余白 [0,p] → [0,p+s]・炎 [p,q] → [p+s,q+s]・右の余白 [q,W] → [q+s,W] (端は動かない = 継ぎ目が出ない)。
      ctx.drawImage(cbuf, 0, y, p, rh, x0, top + y, p + s, rh)
      ctx.drawImage(cbuf, p, y, q - p, rh, x0 + p + s, top + y, q - p, rh)
      ctx.drawImage(cbuf, q, y, W - q, rh, x0 + q + s, top + y, W - q - s, rh)
    }
    ctx.restore()
  }
}

// ===== ④⑤ 世界の層 (床の上・キャラの後ろ) =====
// ④ 霞 = 前縁に立つ「押された空気の幕」(見本の絵の縦のかすみ)。進む側の縁はやわらかく (18px で立ち上がる)、
//   後ろへ長く薄く尾を引く。上ほど前へ少し倒れる (圧が外へ押し出す形)。輪・線の形にはしない (縁に明るい筋を作らない)。
//   形は波の強さごとに一度だけ作って使い回す (毎フレームの乱数なし)。明るさは前縁の強さ k だけで変わる。
const VEIL = { lengthPx: 200, riseFrontPx: 40, peakFromFrontPx: 34, heightMul: 1.3, leanPx: 30, tailHeight: 0.42, blurPx: 16 } as const
const veilCache = new Map<string, HTMLCanvasElement>()
function veilSprite(bossH: number): HTMLCanvasElement {
  const H = Math.round(bossH * VEIL.heightMul)
  const key = String(H)
  const hit = veilCache.get(key)
  if (hit) return hit
  const S = 2 // 内部の解像度 (世界 1px = 2px)
  const c = document.createElement('canvas')
  c.width = VEIL.lengthPx * S
  c.height = H * S
  const g = c.getContext('2d')!
  const L = VEIL.lengthPx
  // 横 (左 = 後ろ・右 = 進む側)。いちばん明るい所は前縁の 34px 後ろ。前縁へは 40px かけてやわらかく消える (縁に筋を作らない)。
  const hg = g.createLinearGradient(0, 0, c.width, 0)
  const stop = (x: number, a: number): void => hg.addColorStop(Math.max(0, Math.min(1, x / L)), `rgba(226,232,248,${a})`)
  stop(0, 0)
  stop(L * 0.35, 0.10)
  stop(L * 0.6, 0.3)
  stop(L - VEIL.peakFromFrontPx - 24, 0.7)
  stop(L - VEIL.peakFromFrontPx, 1)
  stop(L - VEIL.peakFromFrontPx + (VEIL.peakFromFrontPx) * 0.5, 0.6)
  stop(L, 0)
  g.fillStyle = hg
  g.fillRect(0, 0, c.width, c.height)
  // 形 (destination-in): 上の縁は、尾 (後ろ) で低く・前縁へ向かって高く盛り上がる波頭の形。ぼかして輪郭を作らない。
  const m = document.createElement('canvas')
  m.width = c.width
  m.height = c.height
  const mg = m.getContext('2d')!
  mg.filter = `blur(${VEIL.blurPx * S}px)`
  const grd = mg.createLinearGradient(0, 0, 0, c.height)
  grd.addColorStop(0, 'rgba(255,255,255,0.35)')
  grd.addColorStop(0.45, 'rgba(255,255,255,0.8)')
  grd.addColorStop(0.85, 'rgba(255,255,255,1)')
  grd.addColorStop(1, 'rgba(255,255,255,0.75)')
  mg.fillStyle = grd
  mg.beginPath()
  const yTail = c.height * (1 - VEIL.tailHeight)
  const yTop = c.height * 0.06
  mg.moveTo(-40, c.height + 40)
  mg.lineTo(-40, yTail)
  mg.bezierCurveTo(c.width * 0.45, yTail, c.width * 0.72, yTop, c.width * 0.86, yTop)
  mg.quadraticCurveTo(c.width * 0.97, yTop, c.width + 40, c.height * 0.3)
  mg.lineTo(c.width + 40, c.height + 40)
  mg.closePath()
  mg.fill()
  g.globalCompositeOperation = 'destination-in'
  g.drawImage(m, 0, 0)
  veilCache.set(key, c)
  return c
}

// ④' 霞を靄の絵で (比較版 ?execRoarHaze=mist・10-29)。前縁が床の決まった位置 (波と番号で決まる) を通ると、そこから靄が1本立ちのぼり、
//   濃い→ほどける→薄れるの3コマで消える。前縁そのものにも1本付いて一緒に進む (押された空気の先頭)。
//   色は前の幕と同じ銀白を screen で重ねる。上ほど前へ倒す (圧が外へ押す形)。
//   10-30 作者「もうちょっと濃くても良いかも」: 本数 7→10・寿命 0.9→1.05 秒・濃さ 0.9/1.15→1.35/1.75 (1 を超えた分は同じ絵を重ねて濃くする)。
//   10-30 作者 2回目「濃さは良くなった。ただ、蒸気っぽくて衝撃波っぽくない」: 湯気に見えたのは、後ろの靄が長く残って上へ伸びていたから。
//     → 前縁に濃い壁を2枚重ね、強く前へ倒す (押されて吹き飛ぶ形)。後ろの靄は 0.4 秒だけ・上へ伸びず、前へ吹き流されながら低くつぶれて横へ広がる。
//     濃さ (重ね塗りの量) は前の答えのまま。
const PLUME = { perSide: 10, lifeS: 0.4, heightMul: 1.05, widthMul: 1.5, leadAlpha: 1.75, trailAlpha: 1.5, lean: 0.8 } as const
const PLUME_RGB = '226,232,248'
function drawPlume(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, floorY: number, h: number, side: 1 | -1, alpha: number, wMul = 1): void {
  const w = h * (img.naturalWidth / img.naturalHeight) * PLUME.widthMul * wMul
  ctx.save()
  ctx.translate(x, floorY)
  ctx.scale(side, 1)
  ctx.transform(1, 0, -PLUME.lean, 1, 0, 0)   // y が上 (負) ほど x が前へ
  const tint = tinted(img, PLUME_RGB)
  for (let a = alpha; a > 0.01; a -= 1) drawMat(ctx, tint, 0, 0, w, h, 0.5, 1, Math.min(1, a), 0, false, 'screen')
  ctx.restore()
}
function drawRoarHazeMist(ctx: CanvasRenderingContext2D, fr: AirFront, bossH: number, speedPxPerSec: number, gain: number): void {
  const floorY = ARENA.floorY
  const H = bossH * PLUME.heightMul
  // 先頭の壁: 前縁と一緒に進む2枚。前の1枚は濃く細く、すぐ後ろの1枚は低く横へ広い (圧の厚み)。形は波ごとに決まる。
  const lead = mistSprite(fr.hitId + (fr.side > 0 ? 1 : 0), 0)
  if (lead) drawPlume(ctx, lead, fr.x - fr.side * 10, floorY, H, fr.side, gain * fr.k * PLUME.leadAlpha, 0.8)
  const lead2 = mistSprite(fr.hitId + (fr.side > 0 ? 2 : 3), 1)
  if (lead2) drawPlume(ctx, lead2, fr.x - fr.side * 46, floorY, H * 0.7, fr.side, gain * fr.k * PLUME.leadAlpha * 0.7, 1.6)
  // 後ろの靄: 置き場所 ρ_j は波と番号で決まり、前縁が通った時刻から歳を取る。上へは伸びず、前へ吹き流されて低くつぶれる。
  for (let j = 0; j < PLUME.perSide; j += 1) {
    const seed = fr.hitId * 7.9 + j * 3.17 + (fr.side > 0 ? 41 : 0)
    const rho = ((j + 0.3 + 0.5 * hash01(seed)) / PLUME.perSide) * fr.edge
    if (fr.overWall > 0 && rho > fr.r) continue
    const age = (fr.r + fr.overWall - rho) / speedPxPerSec
    if (age < 0 || age > PLUME.lifeS) continue
    const u = age / PLUME.lifeS
    const step = u < 0.22 ? 0 : u < 0.55 ? 1 : 2
    const img = mistSprite(Math.floor(hash01(seed + 1) * MIST_FORMS), step)
    if (!img) continue
    const fadeIn = Math.min(1, age / 0.06)
    const fadeOut = 1 - u ** 1.6
    const h = H * (0.6 + 0.25 * hash01(seed + 2)) * (1 - 0.5 * u)   // つぶれながら
    const x = fr.originX + fr.side * (rho + 70 * (1 - Math.exp(-age / 0.12)))   // 前へ吹き流される (すぐ止まる)
    drawPlume(ctx, img, x, floorY, h, fr.side, gain * PLUME.trailAlpha * fadeIn * fadeOut * (0.7 + 0.3 * hash01(seed + 3)), 1 + 1.2 * u)
  }
}

// 比較版 ?execRoarWave=ring の見た目 (10-30 作者「コードで書いたエフェクトだって一目でわかる。スピードは悪くない」)。
//   線の輪・閃き・丸いグラデーションの粉は描かない。見せるのは「背景の歪み (上の applyRoarAirBackground)」と
//   「床の土ぼこりの絵 (dust_kick 連番) が前縁に蹴られて外へ押し出される」の2つだけ。かけら (dust_bit) が前縁の前へ低く跳ねる。
//   絵の素材が足りない所 (空気の歪みの縁・押される塵の帯) は Codex 発注待ち＝docs/research/2026-10-30-exec-roar-fx-no-code-look.md
const RING_DUST = { perSide: 9, lifeS: 0.5, sizeMul: 0.42, push: 60, rgb: '150,142,150' } as const
// 10-31 作者「床の砂とハエが押されているその絵が、現状薄く表示されすぎている。もうちょっとはっきり、濃く出してほしい」:
//   比較版 = DEV ?execRoarDust=1|2|3。新しい絵は足さず、上の土ぼこり (kick) とかけら (bit・作者の言う「ハエ」と見立てた) を
//   明るく・濃く・大きく・多くする。濃さが 1 を超えた分は同じ絵を重ねる。
//   10-31 作者の答え = 濃さ 1 (答えるページ 1JX68V9HqZ7REahFkqhuMG・pick=1) → 配布版の既定は 1。前の薄い版 = DEV ?execRoarDust=0。
const RING_DUST_BOOST = [
  { alpha: 1, size: 1, count: 1, life: 1, bitSize: 1, bits: 1, bitLife: 1, rgb: RING_DUST.rgb, bitRgb: RING_DUST.rgb },
  { alpha: 1.6, size: 1.15, count: 1, life: 1.1, bitSize: 1.5, bits: 2, bitLife: 1.3, rgb: '196,188,192', bitRgb: '214,206,208' },
  { alpha: 2.2, size: 1.3, count: 1.45, life: 1.2, bitSize: 1.9, bits: 2, bitLife: 1.5, rgb: '214,206,208', bitRgb: '230,224,224' },
  { alpha: 3, size: 1.45, count: 1.8, life: 1.3, bitSize: 2.3, bits: 3, bitLife: 1.7, rgb: '230,224,224', bitRgb: '242,238,236' },
] as const
let dustBoost = 1
export function setRoarDustBoost(v: string | null): void { const n = v === null ? 1 : Number(v); dustBoost = n === 0 || n === 2 || n === 3 ? n : 1 }
function drawMatLayered(ctx: CanvasRenderingContext2D, img: CanvasImageSource, x: number, y: number, w: number, h: number, ax: number, ay: number, alpha: number, flipX: boolean): void {
  for (let a = alpha; a > 0.01; a -= 1) drawMat(ctx, img, x, y, w, h, ax, ay, Math.min(1, a), 0, flipX)
}
function drawRingDust(ctx: CanvasRenderingContext2D, fr: AirFront, bossH: number, speedPxPerSec: number, dustA: number): void {
  if (!dustReady()) return
  const floorY = ARENA.floorY
  const B = RING_DUST_BOOST[dustBoost]!
  const perSide = Math.round(RING_DUST.perSide * B.count)
  const lifeS = RING_DUST.lifeS * B.life
  const bitLifeS = 0.22 * B.bitLife
  for (let i = 0; i < perSide; i += 1) {
    const seed = fr.hitId * 5.3 + i * 2.71 + (fr.side > 0 ? 31 : 0)
    const rho = ((i + 0.2 + 0.6 * hash01(seed)) / perSide) * fr.edge
    if (fr.overWall > 0 && rho > fr.r) continue
    const age = (fr.r + fr.overWall - rho) / speedPxPerSec
    if (age < 0 || age > lifeS) continue
    const t = age / lifeS
    const kick = dustSprite('kick', Math.min(3, Math.floor(t * 4)))
    if (!kick) continue
    const size = bossH * RING_DUST.sizeMul * B.size * (0.75 + 0.35 * hash01(seed + 1)) * (1 - 0.5 * rho / Math.max(1, fr.edge))
    const x = fr.originX + fr.side * (rho + RING_DUST.push * (1 - Math.exp(-age / 0.1)))
    const al = dustA * 1.3 * fr.k * Math.min(1, age / 0.03) * (1 - t ** 1.5)
    drawMatLayered(ctx, tinted(kick, B.rgb), x, floorY, size, size * kick.naturalHeight / kick.naturalWidth, 0.18, 0.98, al * B.alpha, fr.side < 0)
    const tb = age / bitLifeS
    if (tb >= 1) continue
    for (let q = 0; q < B.bits; q += 1) {
      const bit = dustSprite('bit', i + q + (tb > 0.5 ? 1 : 0))
      if (!bit) continue
      const w = size * 0.5 * (B.bitSize / B.size) * (q === 0 ? 1 : 0.6 + 0.3 * hash01(seed + 5 + q))
      const reach = q === 0 ? 0.2 + 0.4 * tb : (0.3 + 0.5 * hash01(seed + 7 + q)) * tb
      const hop = q === 0 ? 0 : w * (0.6 + 0.8 * hash01(seed + 9 + q)) * Math.sin(Math.PI * tb)
      drawMatLayered(ctx, tinted(bit, B.bitRgb), x + fr.side * size * reach, floorY - hop, w, w * bit.naturalHeight / bit.naturalWidth, 0.5, 0.98, al * B.alpha * (1 - tb), fr.side < 0)
    }
  }
}

// 塵の帯 (?execRoarWave=wall)。絵の帯の前の縁 (絵の幅に対する割合) をコマごとに前縁へ合わせ、前後のコマを溶かしてつなぐ。
//   進み具合 p = 前縁が届く範囲のどこまで来たか: 0 = 立ち上がり、1/3 = 最も高い、2/3 = 倒れる、1 = 沈む。
const WALL = { widthMul: 2.2, frontAx: [0.69, 0.9, 0.89, 0.82], alpha: 1.5, rgb: '150,142,150' } as const
function drawRingWall(ctx: CanvasRenderingContext2D, fr: AirFront, bossH: number, dustA: number): void {
  if (!roarWallReady()) return
  const p = Math.max(0, Math.min(1, (fr.r + fr.overWall) / Math.max(1, fr.edge)))
  const f = p * 3
  const f0 = Math.min(2, Math.floor(f))
  const mix = f - f0
  const w = bossH * WALL.widthMul * (0.6 + 0.4 * Math.min(1, p * 3))
  const al = dustA * WALL.alpha * Math.min(1, fr.k * 2.5)
  for (const [stage, wgt] of [[f0, 1 - mix], [f0 + 1, mix]] as const) {
    const img = roarWallSprite(stage)
    if (!img || wgt <= 0.01) continue
    drawMat(ctx, tinted(img, WALL.rgb), fr.x, ARENA.floorY, w, w * img.naturalHeight / img.naturalWidth, WALL.frontAx[stage]!, 0.98, al * wgt, 0, fr.side < 0)
  }
}

const DUST_PER_SIDE = 44
const DUST_LIFE_S = 0.75
const DUST_PEAK_S = 0.14

/** 前縁の霞と足もとの砂ぼこり。drawHazards の直後 (世界の層・キャラの後ろ) に呼ぶ。 */
export function drawRoarAirWorld(ctx: CanvasRenderingContext2D, fronts: readonly AirFront[], bossH: number, speedPxPerSec: number): void {
  if (fronts.length === 0) return
  const floorY = ARENA.floorY
  const veil = veilSprite(bossH)
  const vh = veil.height / 2
  for (const fr of fronts) {
    const g = GAIN[fr.level]
    // ④ 霞の幕。原点 = 前縁の床。右向きに作った絵を、左へ進む前縁では左右反転。上ほど前へ倒す (せん断)。
    const a = g.haze * fr.k
    if (ringLook) {
      drawRingDust(ctx, fr, bossH, speedPxPerSec, g.dustA * (wallLook ? 0.6 : 1))
      if (wallLook) drawRingWall(ctx, fr, bossH, g.dustA)
      continue
    } else if (hazeLook === 'mist' && mistReady()) {
      drawRoarHazeMist(ctx, fr, bossH, speedPxPerSec, g.haze)
    } else if (a >= 0.004) {
      ctx.save()
      ctx.globalCompositeOperation = 'screen'
      ctx.globalAlpha = Math.min(1, a)
      ctx.translate(fr.x, floorY)
      ctx.scale(fr.side, 1)
      ctx.transform(1, 0, -VEIL.leanPx / vh, 1, 0, 0)   // y が上 (負) ほど x が前へ
      ctx.drawImage(veil, -VEIL.lengthPx, -vh, VEIL.lengthPx, vh)
      ctx.restore()
    }
    // ⑤ 砂ぼこり: 粒 i は中心から ρ_i の所で前縁に蹴られ、外へ転がって浮き、ゆっくり沈む。位置は波と番号と時刻だけで決まる。
    //   灰色の粉 (光ではない) を普通に重ねる。前縁の直後がいちばん濃い = 波頭。
    ctx.save()
    for (let i = 0; i < DUST_PER_SIDE; i += 1) {
      const seed = fr.hitId * 13.7 + i * 4.31 + (fr.side > 0 ? 71 : 0)
      // 粒の置き場所は波ごとに決まっている (前縁の動きで並びが変わらない)。前縁がそこを通った時刻から歳を取る。
      const rho = ((i + hash01(seed)) / DUST_PER_SIDE) * fr.edge
      if (fr.overWall > 0 && rho > fr.r) continue   // 壁の向こうの粒は出さない
      const age = (fr.r + fr.overWall - rho) / speedPxPerSec
      if (age < 0 || age > DUST_LIFE_S) continue
      const u = age / DUST_LIFE_S
      const drift = (18 + 30 * hash01(seed + 1)) * (1 - Math.exp(-age / 0.2))
      const peak = g.dustPx * (0.55 + 0.45 * hash01(seed + 2))
      const lift = age < DUST_PEAK_S
        ? peak * (1 - (1 - age / DUST_PEAK_S) ** 2)
        : peak * (1 - ((age - DUST_PEAK_S) / (DUST_LIFE_S - DUST_PEAK_S)) ** 1.4)
      const x = fr.originX + fr.side * (rho + drift)
      const rad = 11 + 18 * u + 5 * hash01(seed + 3) + peak * 0.35
      const y = floorY - 2 - lift * 0.5
      const fadeIn = Math.min(1, age / 0.04)
      const fadeOut = 1 - u ** 1.3
      const al = g.dustA * fr.k * fadeIn * fadeOut * (0.55 + 0.45 * hash01(seed + 4))
      if (al < 0.004) continue
      const grad = ctx.createRadialGradient(x, y, 0, x, y, rad)
      grad.addColorStop(0, `rgba(176,170,178,${al.toFixed(4)})`)
      grad.addColorStop(0.6, `rgba(160,154,166,${(al * 0.45).toFixed(4)})`)
      grad.addColorStop(1, 'rgba(150,144,158,0)')
      ctx.fillStyle = grad
      ctx.beginPath()
      // 横長・低く (膝より下)。浮いた分だけ少し縦へ。
      ctx.ellipse(x, y - rad * 0.25, rad * 1.9, rad * 0.5 + lift * 0.25, 0, 0, Math.PI * 2)
      ctx.fill()
      // 細かい粒: 前縁の前へ低く跳ねる2粒 (蹴られてから 0.4 秒だけ)。
      if (age < 0.4) {
        for (let q = 0; q < 2; q += 1) {
          const sq = seed + 10 + q * 3.3
          const t = age / 0.4
          const gx = x + fr.side * (10 + 26 * hash01(sq)) * t
          const gy = floorY - 2 - (6 + peak * 0.7 * hash01(sq + 1)) * Math.sin(Math.PI * t)
          ctx.fillStyle = `rgba(196,190,198,${(al * 1.4 * (1 - t)).toFixed(4)})`
          ctx.beginPath()
          ctx.arc(gx, gy, 1 + hash01(sq + 2) * 1.2, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }
    ctx.restore()
  }
}
